import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@restworld/feature-authentication';
import { isDev } from './use-is-dev-server';
import { Router } from 'next/router';
import posthog from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
export default function usePostHogProvider() {
  var _useAuth = useAuth(),
      user = _useAuth.user,
      isAuthenticated = _useAuth.isAuthenticated;

  var posthogClient = usePostHog();
  var oldUrlRef = useRef(''); // Tracks whether posthog.init() has finished. Without this, on-mount captures
  // (those fired from a child useEffect) race the parent provider's init useEffect
  // and get silently dropped — child effects run before parent effects.

  var _useState = useState(false),
      ready = _useState[0],
      setReady = _useState[1];

  useEffect(function () {
    var initPostHog = function initPostHog() {
      var _process$env$NEXT_PUB;

      if (posthog.__loaded) return;
      posthog.init((_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_POSTHOG_KEY) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : '', {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        loaded: function loaded(posthog) {
          if (isDev) posthog.debug();
          setReady(true);
        },
        autocapture: false,
        opt_in_site_apps: true
      });
    }; // PostHog initialises unconditionally for every user, on every page,
    // independent of Iubenda consent. Restores the pre-2026-06-10 behavior so
    // analytics are captured on /getting-started (where the Iubenda banner is
    // intentionally not loaded) and across the rest of the app.


    initPostHog();
    oldUrlRef.current = window.location.href; // Pass the destination URL explicitly so shallow routing (e.g. /getting-started?step=X)
    // can't race PostHog's own window.location lookup and register the wrong step.

    var handleRouteChange = function handleRouteChange(url) {
      if (!posthog.__loaded) return;
      var fullUrl = window.location.origin + url;
      posthog.capture('$pageview', {
        $current_url: fullUrl
      });
      oldUrlRef.current = fullUrl;
    };

    var handleRouteChangeStart = function handleRouteChangeStart() {
      if (!posthog.__loaded || !oldUrlRef.current) return;
      posthog.capture('$pageleave', {
        $current_url: oldUrlRef.current
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChange);
    Router.events.on('routeChangeStart', handleRouteChangeStart);
    return function () {
      Router.events.off('routeChangeComplete', handleRouteChange);
      Router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, []);
  useEffect(function () {
    if (!isAuthenticated || !user) return; // Don't send identifying traits before Measurement consent loads PostHog.
    // On consent, Iubenda reloads the page and this effect re-runs once loaded.

    if (!posthog.__loaded) return;
    posthogClient === null || posthogClient === void 0 ? void 0 : posthogClient.identify(user.id, {
      email: user.email,
      name: user.name,
      first_name: user.name,
      last_name: user.surname,
      created: user.inserted_at,
      avatar: user.picture,
      type: user.type,
      phone: user.phone_number,
      organization_source: user.organization_source,
      using_ppu_business_model: user === null || user === void 0 ? void 0 : user.using_ppu_business_model
    });

    if (user.organization_id) {
      posthogClient === null || posthogClient === void 0 ? void 0 : posthogClient.group('organization', user.organization_id, {
        name: user.business_name,
        using_ppu_business_model: user.using_ppu_business_model
      });
    }
  }, [isAuthenticated, user, posthogClient]);
  var trackEvent = useCallback(function (eventName, eventProperties) {
    if (!posthogClient || !ready) return;
    posthogClient.capture(eventName, eventProperties);
  }, [posthogClient, ready]);
  return {
    trackEvent: trackEvent
  };
}